import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetAllContractHierarchyRulesQuery,
  useDeleteContractHierarchyRuleMutation,
} from "store/queries/contractHierarchyRules";
import { IContractHierarchyRule } from "types/ContractHierarchyRule/ContractHierarchyRule";

interface IHierarchyRulesetRow {
  key: string;
  providerId: string;
  providerName: string;
  payerId: number;
  payerName: string;
  ruleCount: number;
  ruleSummary: string;
  rules: IContractHierarchyRule[];
}

export function HierarchyRulesManager() {
  const [selectedItems, setSelectedItems] = useState<IHierarchyRulesetRow[]>(
    []
  );
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [deleteRule] = useDeleteContractHierarchyRuleMutation();
  const navigate = useNavigate();

  const {
    data: rulesResponse,
    isLoading,
    refetch,
  } = useGetAllContractHierarchyRulesQuery();

  const groupedRows: IHierarchyRulesetRow[] = useMemo(() => {
    if (!rulesResponse?.data) return [];

    const groups = new Map<string, IContractHierarchyRule[]>();

    rulesResponse.data.forEach((rule) => {
      const key = `${rule.providerId}__${rule.payerId}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(rule);
    });

    return Array.from(groups.entries()).map(([key, rules]) => {
      const first = rules[0];
      return {
        key,
        providerId: first.providerId,
        providerName: first.provider?.name || "",
        payerId: first.payerId,
        payerName: first.payer?.name || "",
        ruleCount: rules.length,
        ruleSummary: rules.map((r) => r.name).join(", "),
        rules,
      };
    });
  }, [rulesResponse]);

  const handleDeleteGroups = async () => {
    setIsLoading(true);
    try {
      const allRuleIds = selectedItems.flatMap((group) =>
        group.rules.map((r) => r.id)
      );
      const deletePromises = allRuleIds.map((id) => deleteRule(id));
      await Promise.all(deletePromises);
      await refetch();
      toastElement?.show({
        severity: "success",
        summary: "Deleted!",
        detail: "Hierarchy ruleset(s) deleted successfully.",
      });
    } catch (error) {
      await refetch();
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Error while deleting hierarchy rulesets",
      });
    } finally {
      setIsLoading(false);
      setSelectedItems([]);
    }
  };

  const editBodyTemplate = (row: IHierarchyRulesetRow) => (
    <Button
      className="p-button-link"
      onClick={() =>
        navigate(
          `/payer-rules/hierarchy-rules/ruleset/${row.providerId}/${row.payerId}`
        )
      }
    >
      <p className="small-text">View / Edit</p>
    </Button>
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <DataTable
        rows={10}
        paginator
        stripedRows
        className="p-fluid"
        filterDisplay="row"
        value={groupedRows}
        responsiveLayout="scroll"
        style={{ cursor: "pointer" }}
        selection={selectedItems}
        emptyMessage="No hierarchy rulesets to show."
        selectionMode="single"
        dataKey="key"
        onSelectionChange={(event) => setSelectedItems(event.value)}
      >
        <Column selectionMode="multiple" headerStyle={{ width: "3em" }} />
        <Column
          filter
          field="providerName"
          style={{ minWidth: "200px" }}
          header="Provider Name"
          filterPlaceholder="Search by provider"
        />
        <Column
          filter
          field="payerName"
          style={{ minWidth: "200px" }}
          header="Payer Name"
          filterPlaceholder="Search by payer"
        />
        <Column
          field="ruleCount"
          header="Rules"
          style={{ maxWidth: "100px" }}
          sortable
        />
        <Column
          filter
          field="ruleSummary"
          header="Rule Summary"
          style={{ minWidth: "250px" }}
          filterPlaceholder="Search by rule name"
        />
        <Column style={{ maxWidth: "120px" }} body={editBodyTemplate} />
      </DataTable>
      <div className="flex justify-content-end">
        <Button
          label="Delete"
          className="p-button-outlined mt-4"
          disabled={!selectedItems.length}
          onClick={() => {
            confirmDialog({
              header: "Confirmation",
              icon: "pi pi-exclamation-triangle",
              message:
                "Are you sure you want to delete all rules in the selected rulesets?",
              accept: handleDeleteGroups,
            });
          }}
        />
      </div>
    </>
  );
}
